import type { ComponentType, LazyExoticComponent, ReactNode } from 'react';
import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { AppLayout } from '@/common/components/layout/AppLayout';

import { AppLayoutComposer } from './AppLayoutComposer';
import { routes as routeDefinitions } from './routes';
import type { RouteConfig } from './types';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

/**
 * Loading component displayed during lazy loading
 */
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <p className="text-muted-foreground text-sm">Loading...</p>
  </div>
);

/**
 * 404 Not Found page component
 */
const NotFoundPage = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="text-center">
      <h1 className="text-4xl font-bold">404</h1>
      <p className="text-muted-foreground mt-2">Page not found</p>
    </div>
  </div>
);

/**
 * Flattened route configuration with accumulated layouts
 */
interface FlatRouteConfig {
  path: string;
  element?: LazyExoticComponent<ComponentType<unknown>>;
  layouts?: LazyExoticComponent<ComponentType<{ children: ReactNode }>>[];
  noRootLayout?: boolean;
}

/**
 * Flattens nested route configuration into a flat list
 * Accumulates layouts from parent routes as it traverses the tree
 *
 * @param routes - Array of nested route configurations
 * @param parentPath - Accumulated path from parent routes
 * @param parentLayouts - Accumulated layouts from parent routes
 * @returns Flat array of route configurations with accumulated layouts
 */
const flattenRoutes = (
  routes: RouteConfig[],
  parentPath = '',
  parentLayouts: LazyExoticComponent<ComponentType<{ children: ReactNode }>>[] = []
): FlatRouteConfig[] => {
  const flatRoutes: FlatRouteConfig[] = [];

  for (const route of routes) {
    const fullPath = parentPath + route.path;
    const currentLayouts = route.layout ? [...parentLayouts, route.layout] : parentLayouts;

    const hasChildren = route.children && route.children.length > 0;

    // Case 1: Has element - add as renderable route
    if (route.element) {
      flatRoutes.push({
        path: fullPath,
        element: route.element,
        layouts: currentLayouts.length > 0 ? currentLayouts : undefined,
        noRootLayout: route.noRootLayout,
      });
    }
    // Case 2: Layout only without children - render layout with empty content
    else if (route.layout && !hasChildren) {
      flatRoutes.push({
        path: fullPath,
        element: undefined,
        layouts: currentLayouts.length > 0 ? currentLayouts : undefined,
        noRootLayout: route.noRootLayout,
      });
    }
    // Case 3: Neither element nor layout and no children - 404 route
    else if (!route.layout && !hasChildren) {
      flatRoutes.push({
        path: fullPath,
        element: undefined,
        layouts: undefined,
        noRootLayout: route.noRootLayout,
      });
    }

    // Recursively flatten children
    if (hasChildren) {
      flatRoutes.push(...flattenRoutes(route.children!, fullPath, currentLayouts));
    }
  }

  return flatRoutes;
};

/**
 * Main application router component
 *
 * Features:
 * - Flattens nested route configuration into React Router routes
 * - Accumulates layouts from parent routes
 * - Separates routes with noRootLayout flag (e.g., login page)
 * - Wraps all routes with Suspense for lazy loading
 * - Provides catch-all redirect to home
 * - Renders 404 page for undefined routes
 *
 * Route Structure:
 * - Routes with noRootLayout: Rendered directly without root layout wrapper
 * - Routes with root layout: Wrapped in AppLayout with nested layouts
 * - Catch-all route: Redirects to home page
 *
 * @param routes - Array of route configurations (imported from ./routes/index.ts)
 */
export const AppRouter = () => {
  // Use imported route definitions
  const routes: RouteConfig[] = routeDefinitions;

  const flatRoutes = flattenRoutes(routes);

  return (
    <Suspense fallback={<PageLoader />}>
      <SentryRoutes>
        {/* Routes that bypass root layout (e.g., login page) */}
        {flatRoutes
          .filter(({ noRootLayout }) => noRootLayout)
          .map(({ path, element: Page }) => (
            <Route key={path} path={path} element={Page ? <Page /> : <NotFoundPage />} />
          ))}

        {/* Routes with root layout */}
        <Route element={<AppLayout />}>
          {flatRoutes
            .filter(({ noRootLayout }) => !noRootLayout)
            .map(({ path, element: Page, layouts }) => {
              let content;
              if (Page) {
                content = <Page />;
              } else if (layouts && layouts.length > 0) {
                content = null; // Layout only
              } else {
                content = <NotFoundPage />;
              }

              return (
                <Route
                  key={path}
                  path={path}
                  element={<AppLayoutComposer nested={layouts}>{content}</AppLayoutComposer>}
                />
              );
            })}
        </Route>

        {/* Catch-all redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </SentryRoutes>
    </Suspense>
  );
};
